import { Helmet } from "react-helmet-async";
import { useMutation, useQuery } from "react-query";
import { useMemo, useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import contractService from "../../services/contract";
import useNotification from "../../hooks/useNotification";

const formatDateValue = (value?: string) => {
  if (!value) {
    return "-";
  }

  const formatted = dayjs(value).format("YYYY-MM-DD");
  return formatted === "Invalid Date" ? value : formatted;
};

const formatCurrencyValue = (value?: string) => {
  if (!value) {
    return "-";
  }

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return value;
  }

  return `Rp ${new Intl.NumberFormat("id-ID", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numericValue)}`;
};

const ACTIONABLE_STATUS = "employee_review";

type CompletionState = {
  action: "signed" | "rejected";
  message: string;
};

const mapFetchError = (status?: number, message?: string) => {
  if (status === 404) {
    return "invalid";
  }

  if (status === 410) {
    return "expired";
  }

  if (status === 409 || status === 400) {
    return "non_actionable";
  }

  return message || "Unable to load contract review data.";
};

const mapMutationError = (status?: number, fallback?: string) => {
  switch (status) {
    case 404:
      return "Invalid contract review link.";
    case 410:
      return "This review link has expired.";
    case 409:
      return "This review token has already been used.";
    case 400:
      return fallback || "Invalid request. Please verify your input.";
    default:
      return "Something went wrong. Please try again later.";
  }
};

export default function ContractReviewPublic() {
  const { token = "" } = useParams<{ token: string }>();
  const notification = useNotification();

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [completion, setCompletion] = useState<CompletionState | null>(null);
  const [previewError, setPreviewError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const contractQuery = useQuery(
    ["public-contract-review", token],
    () => contractService.getPublicContractByToken(token),
    {
      enabled: !!token,
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  const contract = contractQuery.data?.data;
  const fetchError = contractQuery.error as any;

  // Reset preview error when new contract is loaded
  useEffect(() => {
    setPreviewError(false);
    
    // Cleanup blob URLs on unmount
    return () => {
      if (contract?.preview_url?.startsWith('blob:')) {
        URL.revokeObjectURL(contract.preview_url);
      }
    };
  }, [contract]);

  const reviewState = useMemo(() => {
    if (contractQuery.isLoading) {
      return "loading";
    }

    if (!token) {
      return "invalid";
    }

    if (contractQuery.isError) {
      return mapFetchError(
        fetchError?.response?.status,
        fetchError?.response?.data?.message
      );
    }

    if (!contract) {
      return "invalid";
    }

    if (contract.token_used || contract.status !== ACTIONABLE_STATUS) {
      return "non_actionable";
    }

    return "ready";
  }, [contract, contractQuery.isError, contractQuery.isLoading, fetchError, token]);

  const signMutation = useMutation(contractService.signPublicContract, {
    onSuccess: (response) => {
      setCompletion({
        action: "signed",
        message: response?.message || "Contract signed successfully.",
      });
      notification.onOpen({
        message: response?.message || "Contract signed successfully.",
        type: "success",
        position: "top",
      });
    },
    onError: (error: any) => {
      const message = mapMutationError(
        error?.response?.status,
        error?.response?.data?.message
      );
      notification.onOpen({
        message,
        type: "error",
        position: "top",
      });
    },
  });

  const rejectMutation = useMutation(contractService.rejectPublicContract, {
    onSuccess: (response) => {
      setCompletion({
        action: "rejected",
        message: response?.message || "Contract review submitted.",
      });
      notification.onOpen({
        message: response?.message || "Contract review submitted.",
        type: "success",
        position: "top",
      });
    },
    onError: (error: any) => {
      const message = mapMutationError(
        error?.response?.status,
        error?.response?.data?.message
      );
      notification.onOpen({
        message,
        type: "error",
        position: "top",
      });
    },
  });

  const isSubmitting = signMutation.isLoading || rejectMutation.isLoading;

  const contractDetails = useMemo(() => {
    if (!contract) {
      return [];
    }

    return [
      { label: "Contract ID", value: contract.contract_id },
      { label: "Name", value: contract.name },
      { label: "Date of Birth", value: formatDateValue(contract.dob) },
      { label: "Place of Birth", value: contract.pob || "-" },
      { label: "Last Education", value: contract.last_education || "-" },
      { label: "Gender", value: contract.gender || "-" },
      { label: "Religion", value: contract.religion || "-" },
      { label: "Address", value: contract.address || "-" },
      { label: "ID Number", value: contract.id_number || "-" },
      { label: "Email", value: contract.email || "-" },
      { label: "Phone", value: contract.phone || "-" },
      { label: "Branch", value: contract.branch || "-" },
      { label: "Branch Address", value: contract.branch_address || "-" },
      { label: "Placement Location", value: contract.placement_location || "-" },
      { label: "Salary", value: formatCurrencyValue(contract.salary) },
      { label: "Health Allowance", value: formatCurrencyValue(contract.allowance_health) },
      { label: "Operational Allowance", value: formatCurrencyValue(contract.allowance_operational) },
      { label: "Contract Start", value: formatDateValue(contract.contract_start) },
      { label: "Contract End", value: formatDateValue(contract.contract_end) },
      { label: "Probation", value: typeof contract.is_probation === "boolean" ? (contract.is_probation ? "Yes" : "No") : "-" },
      { label: "Status", value: contract.status || "-" },
    ];
  }, [contract]);

  const handleDownloadContract = () => {
    if (!contract?.preview_url || !token) {
      return;
    }

    // Extract the original file URL (remove Google Docs Viewer wrapper if present)
    let downloadUrl = contract.preview_url;
    if (downloadUrl.includes('docs.google.com/viewer')) {
      const urlMatch = downloadUrl.match(/url=([^&]+)/);
      downloadUrl = urlMatch ? decodeURIComponent(urlMatch[1]) : downloadUrl;
    }

    // Create anchor element and trigger download
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `${contract.name || 'contract'}.docx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const handleSignContract = async () => {
    if (!uploadedFile || !token) {
      notification.onOpen({
        message: "Please upload a signed contract before submitting.",
        type: "warning",
        position: "top",
      });
      return;
    }

    const confirmed = window.confirm("Are you sure you want to submit this signed contract?");
    if (!confirmed) {
      return;
    }

    signMutation.mutate({ token, file: uploadedFile });
  };

  const handleRejectContract = () => {
    if (!token) {
      return;
    }

    if (!rejectionReason.trim()) {
      notification.onOpen({
        message: "Rejection reason is required.",
        type: "warning",
        position: "top",
      });
      return;
    }

    const confirmed = window.confirm("Are you sure you want to reject this contract?");
    if (!confirmed) {
      return;
    }

    rejectMutation.mutate({
      token,
      rejection_reason: rejectionReason.trim(),
    });
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        py: { xs: 2, md: 4 },
        px: { xs: 2, md: 4 },
      }}
    >
      <Helmet>
        <title>Contract Review</title>
      </Helmet>

      <Paper
        elevation={2}
        sx={{
          borderRadius: "16px",
          p: { xs: 2, md: 4 },
          background: "linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)",
        }}
      >
        <Stack spacing={3}>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Public Contract Review
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Review your contract details and provide your decision below.
            </Typography>
          </Box>

          {reviewState === "loading" && (
            <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
              <Stack spacing={2} alignItems="center">
                <CircularProgress />
                <Typography>Loading contract review link...</Typography>
              </Stack>
            </Box>
          )}

          {reviewState === "invalid" && (
            <Alert severity="error">Invalid contract review link.</Alert>
          )}

          {reviewState === "expired" && (
            <Alert severity="warning">This contract review link has expired.</Alert>
          )}

          {reviewState !== "loading" &&
            reviewState !== "ready" &&
            reviewState !== "invalid" &&
            reviewState !== "expired" &&
            reviewState !== "non_actionable" && (
              <Alert severity="error">{reviewState}</Alert>
            )}

          {reviewState === "non_actionable" && (
            <Alert severity="info">
              This contract is no longer actionable. It may have already been processed or is in a different status.
            </Alert>
          )}

          {completion && (
            <Alert severity="success">
              {completion.message} You have successfully {completion.action} this contract.
            </Alert>
          )}

          {reviewState === "ready" && contract && !completion && (
            <Stack spacing={3}>
              <Card
                variant="outlined"
                sx={{
                  borderColor: "#D7E3F4",
                  borderRadius: "20px",
                  overflow: "hidden",
                  background: "linear-gradient(180deg, #FFFFFF 0%, #F7FAFF 100%)",
                  boxShadow: "0 12px 32px rgba(15, 23, 42, 0.06)",
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  <Box
                    sx={{
                      px: { xs: 2, md: 3 },
                      py: { xs: 2.5, md: 3 },
                      background: "linear-gradient(135deg, rgba(37, 99, 235, 0.08) 0%, rgba(14, 165, 233, 0.04) 100%)",
                    }}
                  >
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    justifyContent="space-between"
                    spacing={2.5}
                  >
                    <Stack spacing={1.25}>
                      <Typography variant="h6" fontWeight={700}>
                        {contract.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Public review link for contract #{contract.contract_id}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip label={`Branch: ${contract.branch || "-"}`} size="small" variant="outlined" />
                        <Chip
                          label={`Placement: ${contract.placement_location || "-"}`}
                          size="small"
                          variant="outlined"
                        />
                      </Stack>
                    </Stack>

                    <Stack spacing={1} alignItems={{ xs: "flex-start", md: "flex-end" }}>
                      <Chip label={contract.status} color="warning" size="small" />
                      <Typography variant="body2" color="text.secondary">
                        Start: {formatDateValue(contract.contract_start)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        End: {formatDateValue(contract.contract_end)}
                      </Typography>
                    </Stack>
                  </Stack>
                  </Box>

                  <Divider />

                  <Box
                    sx={{
                      px: { xs: 2, md: 3 },
                      py: { xs: 2.5, md: 3 },
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        md: "repeat(2, minmax(0, 1fr))",
                        lg: "repeat(3, minmax(0, 1fr))",
                      },
                      gap: 2,
                    }}
                  >
                    {contractDetails.map((detail) => (
                      <Box
                        key={detail.label}
                        sx={{
                          p: 2,
                          minHeight: 92,
                          border: "1px solid #E2E8F0",
                          borderRadius: "14px",
                          backgroundColor: "rgba(255, 255, 255, 0.9)",
                          boxShadow: "0 4px 18px rgba(15, 23, 42, 0.04)",
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ letterSpacing: "0.04em", textTransform: "uppercase" }}
                        >
                          {detail.label}
                        </Typography>
                        <Typography
                          variant="body1"
                          fontWeight={600}
                          sx={{ mt: 0.75, color: "#0F172A", wordBreak: "break-word" }}
                        >
                          {detail.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                    Contract Document Preview
                  </Typography>

                  {contract.preview_url && !previewError ? (
                    <Box
                      sx={{
                        width: "100%",
                        height: { xs: 320, md: 480 },
                        border: "1px solid #E2E8F0",
                        borderRadius: "10px",
                        overflow: "hidden",
                        backgroundColor: "#FFF",
                      }}
                    >
                      <iframe
                        title="Contract Preview"
                        src={contract.preview_url}
                        width="100%"
                        height="100%"
                        style={{ border: 0 }}
                        onError={() => setPreviewError(true)}
                      />
                    </Box>
                  ) : (
                    <Stack spacing={1.5}>
                      <Alert severity="info">
                        Document preview is not available. You can download the contract document to review it.
                      </Alert>
                      {contract.preview_url && (
                        <Button
                          variant="outlined"
                          size="small"
                          href={contract.preview_url}
                          download={`contract-${contract.name || 'document'}`}
                        >
                          Download Contract Document
                        </Button>
                      )}
                    </Stack>
                  )}
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Stack spacing={2.5}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                        Download & Upload Signed Contract
                      </Typography>
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={handleDownloadContract}
                        sx={{ mb: 1 }}
                      >
                        Download Contract Document
                      </Button>
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                        Upload Signed Contract
                      </Typography>
                      <input
                        ref={fileInputRef}
                        type="file"
                        hidden
                        accept=".docx,.doc,.pdf"
                        onChange={handleFileUpload}
                      />
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isSubmitting}
                      >
                        {uploadedFile ? `Selected: ${uploadedFile.name}` : "Choose Signed Contract File"}
                      </Button>
                      {uploadedFile && (
                        <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                          ✓ File selected: {uploadedFile.name}
                        </Typography>
                      )}
                    </Box>

                    <Divider />

                    <TextField
                      label="Rejection Reason"
                      multiline
                      minRows={3}
                      value={rejectionReason}
                      onChange={(event) => setRejectionReason(event.target.value)}
                      placeholder="Provide a clear reason if you reject this contract"
                      disabled={isSubmitting}
                      helperText="Required only when rejecting"
                    />

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSignContract}
                        disabled={isSubmitting || !uploadedFile}
                      >
                        {signMutation.isLoading ? "Uploading..." : "Upload Contract"}
                      </Button>

                      <Button
                        variant="outlined"
                        color="error"
                        onClick={handleRejectContract}
                        disabled={isSubmitting || !rejectionReason.trim()}
                      >
                        {rejectMutation.isLoading ? "Submitting..." : "Reject Contract"}
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          )}
        </Stack>
      </Paper>
    </Container>
  );
}
